import {
  CONDITION_FIELDS,
  CONVERSATION_STEP_TYPES,
  FACING_ONLY_TRIGGERS,
  FACING_TRIGGERS,
  NON_CONVERSATION_TRIGGERS,
  UNATTACHED_TRIGGERS,
  VALUELESS_OPERATORS,
  WORKFLOW_FIRST_PATH,
  type WorkflowTrigger,
} from "@sagot/constants";
import { isAction, talksToVisitor } from "./definition-model";
import { exitSlotsOf } from "./exit-slots";
import { edgesWithoutWaiting, loopingPaths } from "./graph-loops";
import type { Problem } from "./problem-model";
import type { Conditions, Definition, Step } from "./types";

/**
 * The rules of the backend's `app/services/workflows/graph.py` that need no database, one function per group, in
 * the same order. A rule here and its twin there must agree: `contracts/workflow-graph-cases.json` holds them to it.
 */

/** Facing steps run on the triggers that talk to a visitor; a reusable workflow runs on whatever calls it (backend `FACING_TRIGGERS`). */
const FACING_TRIGGER_SET: ReadonlySet<string> = new Set([...FACING_TRIGGERS, "reusable"]);
const TIMER_TRIGGER_SET: ReadonlySet<string> = new Set(["visitor_unresponsive", "staff_unresponsive"]);
/** The text of the server's "custom body" check: a `{{variable}}` is swapped for a plain value before the body is read as JSON. */
const PLACEHOLDER = /\{\{\s*[a-z_][a-z0-9_.]*\s*\}\}/g;
/** What Python's `float()` accepts (digits with `_` between them, exponent, `inf`, `nan`), so a value is a number to both sides. */
const NUMBER_TEXT = /^\s*[+-]?(?:(?:\d+(?:_\d+)*(?:\.(?:\d+(?:_\d+)*)?)?|\.\d+(?:_\d+)*)(?:[eE][+-]?\d+(?:_\d+)*)?|inf(?:inity)?|nan)\s*$/i;

interface Located {
  path: string;
  index: number;
  step: Step;
}

const located = (definition: Definition): Located[] =>
  Object.entries(definition.paths).flatMap(([path, body]) => body.steps.map((step, index) => ({ path, index, step })));

const problem = (code: string, where: string, extra: Partial<Pick<Problem, "path" | "stepId" | "params">> = {}): Problem => ({
  code,
  where,
  params: {},
  ...extra,
  source: "client",
});
/** A problem about the step at a position of a path (`paths.A.steps.0`). */
const atStep = (code: string, at: Located, params: Problem["params"] = {}): Problem => problem(code, `paths.${at.path}.steps.${String(at.index)}`, { path: at.path, stepId: at.step.id, params });
/** A problem about a step, named by its id (`step welcome`). */
const ofStep = (code: string, at: Located, params: Problem["params"] = {}): Problem => problem(code, `step ${at.step.id}`, { path: at.path, stepId: at.step.id, params });

// ------------------------------------------------------------------ structure

export function structureProblems(definition: Definition): Problem[] {
  const problems: Problem[] = [];
  if (!(WORKFLOW_FIRST_PATH in definition.paths)) problems.push(problem("no_first_path", `paths.${WORKFLOW_FIRST_PATH}`, { path: WORKFLOW_FIRST_PATH }));
  const seen = new Set<string>();
  for (const at of located(definition)) {
    if (seen.has(at.step.id)) problems.push(atStep("duplicate_step", at, { step: at.step.id }));
    seen.add(at.step.id);
    for (const slot of exitSlotsOf(at.step)) {
      if (slot.target && !(slot.target in definition.paths)) problems.push(atStep("unknown_path", at, { path: slot.target }));
      if (slot.required && !slot.target) problems.push(atStep("exit_unset", at));
    }
  }
  for (const path of loopingPaths(edgesWithoutWaiting(definition))) problems.push(problem("loop", `paths.${path}`, { path, params: { path } }));
  for (const [note, { path }] of Object.entries(definition.notes ?? {})) {
    if (path !== undefined && !(path in definition.paths)) problems.push(problem("note_unknown_path", `notes.${note}`, { params: { path } }));
  }
  return problems;
}

// ------------------------------------------------------------------ the visitor

const hasIntentCondition = (definition: Definition): boolean =>
  definition.settings.conditions.any.some((group) => group.all.some((condition) => condition.field.startsWith("intent.")));

export function facingProblems(definition: Definition, trigger: WorkflowTrigger): Problem[] {
  const problems: Problem[] = [];
  const steps = located(definition).filter((at) => talksToVisitor(at.step));
  if (steps.length > 0 && !FACING_TRIGGER_SET.has(trigger)) problems.push(...steps.map((at) => ofStep("not_facing", at)));
  if (FACING_ONLY_TRIGGERS.has(trigger) && steps.length === 0) problems.push(problem("needs_facing", "settings"));
  if (trigger === "intent_detected" && !hasIntentCondition(definition)) problems.push(problem("needs_intent", "settings"));
  for (const at of steps) {
    const { step } = at;
    if (step.type === "hand_to_person" && ((step.to === "team" && !step.team_id) || (step.to === "user" && !step.user_id))) problems.push(ofStep("missing_target_hand", at));
  }
  return problems;
}

// ------------------------------------------------------------------ actions

/** A custom webhook body is JSON once its `{{variables}}` are plain values (backend `template_is_json`). */
export function templateIsJson(step: Extract<Step, { type: "send_webhook" }>): boolean {
  if (step.body !== "custom") return true;
  if (!step.template) return false;
  try {
    JSON.parse(step.template.replace(PLACEHOLDER, "x"));
  } catch {
    return false;
  }
  return true;
}

/** Every behind-the-scenes action, including the ones inside Apply rules (backend `actions_of`). */
function actionsOf(definition: Definition): { at: Located; action: Step }[] {
  return located(definition).flatMap((at) => {
    if (at.step.type === "apply_rules") return at.step.rules.flatMap((rule) => rule.actions.map((action) => ({ at, action })));
    return isAction(at.step) ? [{ at, action: at.step }] : [];
  });
}

export function actionProblems(definition: Definition, trigger: WorkflowTrigger): Problem[] {
  const problems: Problem[] = [];
  const noConversation = NON_CONVERSATION_TRIGGERS.has(trigger);
  for (const { at, action } of actionsOf(definition)) {
    // The problem sits on the step the author sees (an action inside Apply rules is shown on that step), named by the action's own id.
    const place = (code: string): Problem => problem(code, `step ${action.id}`, { path: at.path, stepId: at.step.id, params: { step: action.id } });
    if (noConversation && CONVERSATION_STEP_TYPES.has(action.type)) problems.push(place("needs_conversation"));
    if (action.type === "assign" && ((action.to === "team" && !action.team_id) || (action.to === "user" && !action.user_id))) problems.push(place("missing_target_assign"));
    if (action.type === "notify_staff" && ((action.to === "team" && !action.team_id) || (action.to === "user" && !action.user_id))) problems.push(place("missing_target_notify"));
    if (action.type === "send_webhook" && !templateIsJson(action)) problems.push(place("bad_template"));
  }
  return problems;
}

// ------------------------------------------------------------------ time

export function timeProblems(definition: Definition, trigger: WorkflowTrigger): Problem[] {
  const problems: Problem[] = [];
  if (TIMER_TRIGGER_SET.has(trigger) && definition.settings.trigger_options?.minutes === undefined) problems.push(problem("needs_minutes", "settings"));
  const conversationless = NON_CONVERSATION_TRIGGERS.has(trigger);
  for (const at of located(definition)) {
    const { step } = at;
    if (step.type === "wait") {
      if (UNATTACHED_TRIGGERS.has(trigger)) problems.push(ofStep("no_wait_here", at));
      if (step.interrupt_on.length > 0 && conversationless) problems.push(ofStep("no_interrupt_here", at));
    }
    if (step.type === "ask_rating" && UNATTACHED_TRIGGERS.has(trigger)) problems.push(ofStep("no_rating_here", at));
    if (step.type === "set_attribute" && conversationless && step.object === "conversation") problems.push(ofStep("needs_conversation", at));
  }
  return problems;
}

// ------------------------------------------------------------------ conditions

const isBlank = (value: unknown): boolean => {
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return !value.some((item) => typeof item === "string" && item.trim() !== "");
  return value === null || value === undefined;
};

const isNumber = (value: unknown): boolean => {
  if (typeof value === "boolean") return false;
  if (typeof value === "number") return true;
  return typeof value === "string" && NUMBER_TEXT.test(value);
};

/** Every "only when" of the workflow with where it sits: the settings, branches and rules. */
function conditionSets(definition: Definition): { where: string; at?: Located; conditions: Conditions }[] {
  const sets: { where: string; at?: Located; conditions: Conditions }[] = [{ where: "settings", conditions: definition.settings.conditions }];
  for (const at of located(definition)) {
    const { step } = at;
    if (step.type === "branch") for (const branch of step.branches) sets.push({ where: `step ${step.id}`, at, conditions: branch.when });
    else if (step.type === "apply_rules") for (const rule of step.rules) sets.push({ where: `step ${step.id}`, at, conditions: rule.when });
  }
  return sets;
}

export function conditionProblems(definition: Definition): Problem[] {
  const problems: Problem[] = [];
  for (const { where, at, conditions } of conditionSets(definition)) {
    const place = at ? { path: at.path, stepId: at.step.id } : {};
    for (const group of conditions.any) {
      for (const condition of group.all) {
        if (VALUELESS_OPERATORS.has(condition.op)) continue;
        if (isBlank(condition.value)) problems.push(problem("needs_value", where, { ...place, params: { field: condition.field } }));
        else if (CONDITION_FIELDS[condition.field] === "number" && !isNumber(condition.value)) problems.push(problem("needs_number", where, { ...place, params: { field: condition.field } }));
      }
    }
  }
  return problems;
}
